import React, { useState, useEffect } from "react";

import AutorenewIcon from "@material-ui/icons/Autorenew";
import Close from "@material-ui/icons/Close";
import Navbar from "components/Navbar";

import { ApiErrors } from "common/api-errors/errors";
import { AddUserDetails } from "common/graphql/queryDescriptors";
import { CommonButton, CommonCheckbox } from "common/components";
import { CommonErrorDialog } from "common/components/CommonErrorDialog";
import { ContentWrapper } from "components/ContentWrapper";
import { errorMessages } from "common/utils/validation";
import { Formik, Form } from "formik";
import { FormProgress } from "../../shared/form-progress/FormProgress";
import { REGISTER_USER_DETAILS_ACTION } from "common/store/user/actions";
import { StoreState } from "common/store/types";
import { useCustomQuery } from "common/store/hooks/useCustomQuery";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import { useAddToWhitelist } from "common/store/user/hooks/useAddToWhitelist";
import { User } from "common/store/user/types";
import { useTranslation } from "react-i18next";
import { Wallet } from "ethers";

import {
  Typography,
  Grid,
  Card,
  IconButton,
  CircularProgress,
  Box,
  Button,
  Dialog,
} from "@material-ui/core";

import {
  RegisterUserInfoSubform,
  RegisterUserProofSubform,
  RegisterUserExperienceSubform,
  RegisterUserInvestSubform,
  RegisterUserAgreementSubform,
} from "./subforms/index";

import "./KnowYourCustomerForm.scss";

export enum RegisterUserStep {
  Info = 0,
  Proof,
  Experience,
  Invest,
  Agreement,
  Count,
}

const RegisterUserSubform = ({
  type,
  values,
  errors,
  touched,
  setFieldValue,
}: any): any => {
  switch (type) {
    case RegisterUserStep.Info:
      return (
        <RegisterUserInfoSubform
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          values={values}
        />
      );
    case RegisterUserStep.Proof:
      return (
        <RegisterUserProofSubform
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          values={values}
        />
      );
    case RegisterUserStep.Experience:
      return (
        <RegisterUserExperienceSubform
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          values={values}
        />
      );
    case RegisterUserStep.Invest:
      return (
        <RegisterUserInvestSubform
          setFieldValue={setFieldValue}
          errors={errors}
          touched={touched}
          values={values}
        />
      );
    case RegisterUserStep.Agreement:
      return <RegisterUserAgreementSubform />;
    default:
      break;
  }

  return "";
};

export const KnowYourCustomerForm = () => {
  //State
  const [openSupport, setOpenSupport] = useState<boolean>(false);
  const [error, setError] = useState("");
  const [step, setStep] = useState(RegisterUserStep.Info);
  const [skipIdNow, setSkipIdNow] = useState<boolean>(false);

  //Hooks
  const history = useHistory();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const storageUserDetails = useSelector<StoreState, User>(
    (state) => state.currentUser
  );

  const {
    execute: executeAddUserDetails,
    loading: addUserLoading,
  } = useCustomQuery(
    AddUserDetails({
      onError: (error: ApiErrors) => {
        if (error === ApiErrors.USER_DUPLICATE_PASSPORT) {
          setError(t("Duplicated passport"));
          setStep(RegisterUserStep.Proof);
        }
      },
      onSuccess: async () => {
        const randomAddress = await Wallet.createRandom().getAddress();
        await executeAddToWhitelist({ variables: { address: randomAddress } });
      },
    })
  );

  const {
    executeAddToWhitelist,
    loading: whitelistLoading,
  } = useAddToWhitelist({
    onSuccess: () => {
      if (!skipIdNow) {
        history.replace("/idnowstatus");
      } else {
        history.goBack();
      }
    },
  });

  //Helpers
  const nextStep = () =>
    step !== RegisterUserStep.Count - 1 ? step + 1 : step;
  const previousStep = () => (step !== RegisterUserStep.Info ? step - 1 : step);

  //Actions
  useEffect(() => {
    if (storageUserDetails.userKnown()) {
      history.goBack();
    }
  }, [storageUserDetails, history]);

  const keys = (): Array<string> => {
    switch (step) {
      case RegisterUserStep.Info:
        return [
          "firstName",
          "lastName",
          "cellphone",
          "birthDate",
          "birthCity",
          "gender",
          "citizenship",
          "taxNumber",
          "notUSTax",
          "politicallyExposed",
        ];
      case RegisterUserStep.Proof:
        return [
          "idType",
          "idDocumentNumber",
          "addressCountry",
          "addressZipcode",
          "addressCity",
          "address",
          "passportIssuingAgency",
          "idDocumentCountry",
        ];
      case RegisterUserStep.Experience:
        return ["experience", "transactionsPerYear", "volumePerYear"];
      case RegisterUserStep.Invest:
        return [
          "employment",
          "prevEmployment",
          "highestEducation",
          "occupationGroup",
        ];
      case RegisterUserStep.Agreement:
        return [];
      default:
        return [];
    }
  };

  const nextButtonText = (): string => {
    switch (step) {
      case RegisterUserStep.Info:
      case RegisterUserStep.Proof:
      case RegisterUserStep.Experience:
      case RegisterUserStep.Invest:
        return t("Next");
      case RegisterUserStep.Agreement:
        return t("I understand and still want to invest");
      default:
        return t("Next");
    }
  };

  const nextButtonClass = (): string => {
    switch (step) {
      case RegisterUserStep.Agreement:
        return "primary--scalable";
      default:
        return "primary";
    }
  };

  const title = (): string => {
    switch (step) {
      case RegisterUserStep.Info:
        return t("Let's get to know you");
      case RegisterUserStep.Proof:
        return t("Let's get to know you");
      case RegisterUserStep.Experience:
        return t("Investment experience");
      case RegisterUserStep.Invest:
        return t("Get ready to invest");
      case RegisterUserStep.Agreement:
        return t("Notice");
      default:
        return "";
    }
  };

  const isSubformProcessingData = (): boolean => {
    switch (step) {
      case RegisterUserStep.Info:
      case RegisterUserStep.Proof:
      case RegisterUserStep.Experience:
      case RegisterUserStep.Invest:
        return true;
      case RegisterUserStep.Agreement:
        return false;
      default:
        return false;
    }
  };

  const isFirstStep = (): boolean => {
    return step === 0;
  };

  const isLastStep = (): boolean => {
    return step === RegisterUserStep.Count - 1;
  };

  const showOpenCompanyAccountButton = (): boolean => {
    return !showPreviousButton() && step !== RegisterUserStep.Agreement;
  };

  const showPreviousButton = (): boolean => {
    switch (step) {
      case RegisterUserStep.Info:
        return false;
      case RegisterUserStep.Proof:
      case RegisterUserStep.Experience:
      case RegisterUserStep.Invest:
      case RegisterUserStep.Agreement:
        return true;
      default:
        return false;
    }
  };

  //Validation
  const onValidate = (values: User) => {
    return isSubformProcessingData()
      ? new User(values).validate(keys(), errorMessages)
      : {};
  };

  const onSubmit = async (values: User) => {
    const data = values.addRequest(skipIdNow);

    if (!values.userKnown()) {
      await executeAddUserDetails({
        variables: data,
      });
    }
  };

  const onNext = async (values: any, { setSubmitting, setTouched }: any) => {
    setSubmitting(true);

    if (isSubformProcessingData()) {
      dispatch({
        type: REGISTER_USER_DETAILS_ACTION,
        payload: values,
      });
    }

    if (isLastStep()) {
      onSubmit(values);
    } else {
      setStep(nextStep());
      setTouched({});
    }

    setSubmitting(false);
  };

  const onPrevious = (values: any, { setTouched }: any) => {
    if (isSubformProcessingData()) {
      dispatch({
        type: REGISTER_USER_DETAILS_ACTION,
        payload: values,
      });
    }

    if (!isFirstStep()) {
      setStep(previousStep());
      setTouched({});
    }
  };

  //Options
  const shouldShowPreviousButton = showPreviousButton();

  return (
    <>
      <Navbar />

      <ContentWrapper>
        <Formik
          validate={onValidate}
          onSubmit={onNext}
          initialValues={storageUserDetails}
        >
          {({
            submitForm,
            isSubmitting,
            setFieldValue,
            setTouched,
            setValues,
            values,
            errors,
            touched,
          }) => (
            <Box className="register-user-form-box">
              <Box display="grid">
                <CommonCheckbox
                  id="skipIdNow"
                  values={{ skipIdNow }}
                  setFieldValue={() => setSkipIdNow(!skipIdNow)}
                  label={t("Skip IDNow")}
                  className="centered-in-grid"
                />

                <IconButton
                  className="secondary--small autofill"
                  onClick={() => setValues(new User(User.automatic()))}
                >
                  <AutorenewIcon />
                </IconButton>
              </Box>

              <Card className={"card"}>
                <IconButton
                  className="close-icon"
                  onClick={() => {
                    history.go(-2);
                  }}
                >
                  <Close />
                </IconButton>

                <Typography className="title--centered" variant="subtitle1">
                  {title()}
                </Typography>

                <Form autoComplete="off">
                  <RegisterUserSubform
                    type={step}
                    setFieldValue={setFieldValue}
                    errors={errors}
                    touched={touched}
                    values={values}
                  />
                  <Grid
                    container
                    item
                    direction={shouldShowPreviousButton ? "row" : "column"}
                    spacing={shouldShowPreviousButton ? 6 : 0}
                  >
                    {whitelistLoading || addUserLoading ? (
                      <CircularProgress color="primary" />
                    ) : (
                      <>
                        {shouldShowPreviousButton && (
                          <Grid
                            item
                            lg={6}
                            xs={6}
                            md={6}
                            className="button-container"
                          >
                            <CommonButton
                              onClick={() => onPrevious(values, { setTouched })}
                              isSubmitting={isSubmitting}
                              values={values}
                              errors={errors}
                              touched={touched}
                              text={t("Back")}
                            />
                          </Grid>
                        )}
                        {showOpenCompanyAccountButton() && (
                          <Grid item lg={12} className="button-container">
                            <Button
                              onClick={() => setOpenSupport(true)}
                              style={{}}
                              fullWidth
                              variant="contained"
                            >
                              {t("I am opening this account for a company")}
                            </Button>

                            <Dialog
                              open={openSupport}
                              onClose={() => setOpenSupport(false)}
                            >
                              <Box style={{ padding: 26 }}>
                                <Typography>
                                  {t("Please contact support")}
                                </Typography>
                                <Typography>{t("info@finpro.com")}</Typography>
                              </Box>
                            </Dialog>
                          </Grid>
                        )}
                        <Grid
                          item
                          lg={shouldShowPreviousButton ? 6 : 12}
                          xs={shouldShowPreviousButton ? 6 : false}
                          md={shouldShowPreviousButton ? 6 : false}
                          className="button-container"
                        >
                          <CommonButton
                            onClick={submitForm}
                            isSubmitting={isSubmitting}
                            values={values}
                            errors={errors}
                            touched={touched}
                            text={nextButtonText()}
                            className={nextButtonClass()}
                          />
                        </Grid>
                      </>
                    )}
                  </Grid>
                </Form>

                <CommonErrorDialog error={error} setError={setError} />
              </Card>

              <FormProgress
                maxStep={RegisterUserStep.Count}
                step={step}
                onClick={(goToStep) => {
                  setTouched({});
                  setStep(goToStep);
                }}
              />
            </Box>
          )}
        </Formik>
      </ContentWrapper>
    </>
  );
};
